import CustomerAlternativeRoute from "./CostPrice/CustomerAlternativeRoute";
import React, { useState, useMemo, useEffect } from "react";
import { useSearchParams } from "react-router-dom";
import { useTranslation } from "react-i18next";
import Header from "./CostPrice/Header";
import Cards from "./CostPrice/Cards";
import CostPriceTable from "./CostPrice/CostPriceTable";
import { useLoadCostPrice, useLoadOrderHistory, useLoadRevenueHistory, useLoadMenus, useLoadSubrecipes } from "../../../../utils/dataLoadingUtils";
import { useAppUserDefaultStore, isAllRestaurantsSelected } from "../../../../stores/appUserDefaultStore";
import { usePracticeDataset } from '../../../onboarding/practice/practiceDataset';
import { isPracticeActive, isPracticeId } from "../../../onboarding/practice/practiceGuards";
import { useCostPriceStore } from "../../../../stores/appCostPriceStore";
import { useCostPriceCategoriesStore } from "../../../../stores/appCostPriceCategoriesStore";
import { useRevenueHistory } from "../../../../stores/appRevenueHistoryStore";
import { useFiltersStore } from "../../../../stores/appFiltersStore";
import { calculateCostPriceKPIs, createCostPriceFromPlaceholder, getItemWithHighestMargin, getMostSoldItem, COST_PRICE_TYPE_CHOICE, findOrCreateChoiceCostPrice } from "../../../../utils/costPriceUtils";
import { fetchRevenueItemNames, createCostPriceAnalysis, CostPriceCreateRequest } from "../../../../services/costPrice/costPriceService";
import { DishRowData } from "./costPriceDataTypes";
import { buildCostPriceCsv, buildCostPriceClipboardText } from "./costPriceExport";
import { buildCostPriceDetailedCsv } from "./costPriceDetailedExport";
import { downloadCsv } from "../../../../utils/reports/utilizationCsv";
import CreateCustomCostPriceModal from "../modals/CreateCustomCostPriceModal";
import CostPriceDetailModal from "./CostPrice/CostPriceDetailModal";
import CategoryAssignmentModal from "../../../costPrice/CategoryAssignmentModal";
import { createCostPriceCategory, updateCategoryPositions } from "../../../../services/costPrice/costPriceCategoriesService";
import { updateCostPriceAnalysis } from "../../../../services/costPrice/costPriceService";
import { RevenueSourceType } from "../../../../types/RevenueSourceType";
import CostPriceAutomationModal from "../modals/CostPriceAutomationModal";
import LinkCustomIngredientsBulkModal from "./CostPrice/LinkCustomIngredientsBulkModal";
import LinkAlternativesBulkModal from "./CostPrice/LinkAlternativesBulkModal";
import CostPriceDeleteModal from "../modals/CostPriceDeleteModal";
import AutomationRunBanner from "../automationRun/AutomationRunBanner";
import { useCostPriceDishData } from "../../../../hooks/useCostPriceDishData";
import { useByRestaurantCostData } from "../../../../hooks/useByRestaurantCostData";
import { useOrderHistory } from "../../../../stores/appOrderHistoryStore";
import { useSubrecipesStore } from "../../../../stores/appSubrecipesStore";
import { canAccessCostPriceAutomation } from "../../../../services/accessManagement/accessManagement";
import { useAuth } from "../../../../services/authentication/authContext";
import { useMenusStore } from "../../../../stores/appMenusStore";
import { addItemsToMenu } from "../../../../services/menus/menusService";
import { useRestaurantStore } from "../../../../stores/appRestaurantStore";
import { CostTargets, resolveCostTargets } from "../../../../utils/costTargets";
import { resolveWastePercentage } from "../../../../utils/wasteUtils";
import { getActiveDishNames, getCostPriceMenuMode } from "../../../../utils/costPriceMenuFilter";
import MobilePageHeader from "../../../shared/mobile/MobilePageHeader";
import RestaurantSelector from "../../../common/RestaurantSelector";

const CostPricePageNew = () => {
    const { t } = useTranslation();

    // Load the data
    useLoadCostPrice();
    useLoadOrderHistory();
    useLoadRevenueHistory();
    useLoadMenus();
    useLoadSubrecipes();

    // Connect to stores
    const selectedRestaurant = useAppUserDefaultStore(s => s.restaurant);
    const restaurantId = selectedRestaurant?.id;
    const showExVatOrders = useAppUserDefaultStore(s => s.showExVatOrders);
    const practiceData = usePracticeDataset();
    const liveCostPriceStore = useCostPriceStore();
    const costPriceStore = practiceData ? { ...liveCostPriceStore, ...practiceData } : liveCostPriceStore;
    const activeTab = useCostPriceStore(s => s.activeTab);
    const setActiveTab = useCostPriceStore(s => s.setActiveTab);
    const categoriesStore = useCostPriceCategoriesStore();
    const menusMenus = useMenusStore((s) => s.menus);
    const menusCurrentRestaurantId = useMenusStore((s) => s.currentRestaurantId);
    const menusLoadMenuItems = useMenusStore((s) => s.loadMenuItems);
    // Subscribe to the items map directly so memos depending on it re-run
    // when async loadMenuItems calls finish. Without this, matchesMenuFilter
    // captures an empty idSet on first render and the table looks empty.
    const menusMenuItems = useMenusStore((s) => s.menuItems);
    const liveRevenueHistoryStore = useRevenueHistory();
    const revenueHistoryStore = practiceData ? { ...liveRevenueHistoryStore, ...practiceData } : liveRevenueHistoryStore;
    const matrixFilters = useFiltersStore(s => s.matrixFilters);
    const setMatrixFilters = useFiltersStore(s => s.setMatrixFilters);
    const menuFilters = useFiltersStore(s => s.costPriceMenuFilters);
    const setMenuFilters = useFiltersStore(s => s.setCostPriceMenuFilters);
    const menuFiltersInitForRestaurantId = useFiltersStore(s => s.costPriceMenuFiltersInitForRestaurantId);
    const setMenuFiltersInitForRestaurantId = useFiltersStore(s => s.setCostPriceMenuFiltersInitForRestaurantId);
    const sortColumn = useFiltersStore(s => s.costPriceSortColumn);
    const sortDirection = useFiltersStore(s => s.costPriceSortDirection);
    const setCostPriceSort = useFiltersStore(s => s.setCostPriceSort);
    const menuMode = getCostPriceMenuMode(menuFilters);

    // Access control
    const { backendUser, accountSettings } = useAuth();
    const canAccessAutomation = canAccessCostPriceAutomation(backendUser, accountSettings);

    // Local state
    const [selectedCategoryId, setSelectedCategoryId] = useState<string>("all");
    const categoryRef = React.useRef(selectedCategoryId);
    categoryRef.current = selectedCategoryId;
    const [liveRevenueItems, setRevenueItems] = useState<string[]>([]);
    const revenueItems = practiceData?.revenueItems ?? liveRevenueItems;
    const [showCreateCustomModal, setShowCreateCustomModal] = useState(false);
    const [automationVariant, setAutomationVariant] = useState<'standard' | 'new_menu' | null>(null);
    // Bulk "link custom ingredients" slide-over. Its dry-run spends AI budget, so it sits
    // behind the same `cost_price_automation` gate as the automation entry.
    const [showCustomLinkModal, setShowCustomLinkModal] = useState(false);
    const [showAlternativesLinkModal, setShowAlternativesLinkModal] = useState(false);
    const [showDeleteModal, setShowDeleteModal] = useState(false);
    const [selectedCostPriceId, setSelectedCostPriceId] = useState<string | null>(null);
    // Rows the table is currently showing (all filters + search + sorting applied,
    // across all pages) — the single source for the export / copy actions.
    const [visibleRows, setVisibleRows] = useState<DishRowData[]>([]);

    // Deep-link: `/costprice?detail=<id>` (e.g. from the menu-engineering chart)
    // pre-opens the detail modal. Consume the param once and strip it so the URL
    // normalises and closing/reopening behaves like a normal in-page modal.
    const [searchParams, setSearchParams] = useSearchParams();
    useEffect(() => {
        const detailId = searchParams.get('detail');
        if (detailId && !searchParams.has('customerRequest')) {
            setSelectedCostPriceId(detailId);
            searchParams.delete('detail');
            setSearchParams(searchParams, { replace: true });
        }
    }, [searchParams, setSearchParams]);

    // Guided tour. Opening is restricted to the tour's own practice dish, so a stray event
    // can never swap the user into one of their real dishes. Closing is left unrestricted
    // on purpose: entering or leaving a practice session also closes any stale drawer.
    useEffect(() => {
        const onOpen = (event: Event) => {
            const id = (event as CustomEvent<{ id?: string }>).detail?.id;
            if (id && isPracticeId(id)) setSelectedCostPriceId(id);
        };
        const onClose = () => {
            setSelectedCostPriceId(null);
            // A category dialog opened before the tour must not survive into or out of
            // practice and later assign the example name to a real analysis.
            setEditingItemCategory(null);
        };
        // A category filter the user left on would hide the practice dish from the table,
        // so the tour asks for it, clears it, and hands it back when the branch ends.
        const onSnapshot = (event: Event) => {
            const detail = (event as CustomEvent<{ categoryId?: string }>).detail;
            if (detail) detail.categoryId = categoryRef.current;
        };
        const onResetFilters = () => setSelectedCategoryId('all');
        const onRestoreFilters = (event: Event) => {
            const categoryId = (event as CustomEvent<{ categoryId?: string }>).detail?.categoryId;
            if (categoryId) setSelectedCategoryId(categoryId);
        };
        window.addEventListener('onboarding:cost-snapshot-filters', onSnapshot);
        window.addEventListener('onboarding:cost-reset-filters', onResetFilters);
        window.addEventListener('onboarding:cost-restore-filters', onRestoreFilters);
        window.addEventListener('onboarding:cost-open-dish', onOpen);
        window.addEventListener('onboarding:cost-close-dish', onClose);
        return () => {
            window.removeEventListener('onboarding:cost-open-dish', onOpen);
            window.removeEventListener('onboarding:cost-close-dish', onClose);
            window.removeEventListener('onboarding:cost-snapshot-filters', onSnapshot);
            window.removeEventListener('onboarding:cost-reset-filters', onResetFilters);
            window.removeEventListener('onboarding:cost-restore-filters', onRestoreFilters);
        };
    }, []);

    // BYO = build-your-own. When the user opens a CHOICE from inside a BYO modal,
    // we remember which BYO dish they came from so the close handler can hop back.
    const [parentByoCostPriceId, setParentByoCostPriceId] = useState<string | null>(null);
    const [editingItemCategory, setEditingItemCategory] = useState<{ itemName: string; currentCategoryId: string } | null>(null);

    // Check if "All Restaurants" is selected
    const isAllRestaurants = selectedRestaurant && isAllRestaurantsSelected(selectedRestaurant);

    // Per-restaurant cost targets (food/drinks/overall, falls back to 28% each)
    // and default waste % (falls back to 0) come from the cached restaurant store
    // — no per-page fetch. Empty for the "all restaurants" view.
    const reportSettingsMap = useRestaurantStore(s => s.reportSettings);
    const reportSettings = (practiceData || !restaurantId || isAllRestaurants) ? undefined : reportSettingsMap[restaurantId];
    const costTargets: CostTargets = useMemo(() => resolveCostTargets(reportSettings?.costTargets), [reportSettings]);
    const defaultWastePercentage = useMemo(() => resolveWastePercentage(reportSettings?.wastePercentage), [reportSettings]);

    // Load revenue item names
    useEffect(() => {
        const loadRevenueItems = async () => {
            if (!restaurantId || isAllRestaurants) return;

            try {
                const items = await fetchRevenueItemNames(restaurantId);
                setRevenueItems(items);
            } catch (error) {
                console.error('Failed to load revenue items:', error);
            }
        };

        loadRevenueItems();
    }, [restaurantId, isAllRestaurants]);

    // Initialize the menu filter once per restaurant: pre-select all active menus,
    // or fall back to the Active Dishes mode if the restaurant has none.
    // Only runs once the menus store has finished loading for THIS restaurant
    // (otherwise we'd lock in a default while menus is still the initial empty array).
    // The init gate lives in the filters store so it survives page navigation —
    // navigating away and back must NOT re-apply defaults over the user's choice.
    useEffect(() => {
        if (isPracticeActive() || !restaurantId || isAllRestaurants) return;
        if (menusCurrentRestaurantId !== restaurantId) return;
        if (menuFiltersInitForRestaurantId === restaurantId) return;

        const activeMenuIds = menusMenus.filter(m => m.isActive).map(m => m.id);
        setMenuFilters(activeMenuIds.length > 0 ? activeMenuIds : ['__active_dishes__']);
        setMenuFiltersInitForRestaurantId(restaurantId);
    }, [practiceData, restaurantId, isAllRestaurants, menusMenus, menusCurrentRestaurantId, menuFiltersInitForRestaurantId, setMenuFilters, setMenuFiltersInitForRestaurantId]);

    // Ensure menu items are loaded for every menu currently selected.
    useEffect(() => {
        if (menuMode !== 'menus') return;
        menuFilters.forEach(id => {
            menusLoadMenuItems(id);
        });
    }, [menuMode, menuFilters, menusLoadMenuItems]);

    // Inputs the detailed export re-walks (subrecipe internals are never enriched,
    // so it prices them live against exactly these data sets). Same store reads
    // the enrichment above uses — no extra fetch.
    const orderHistoryList = useOrderHistory(s => s.orderHistoryList);
    const subrecipesById = useSubrecipesStore(s => s.subrecipesById);
    const byRestaurant = useByRestaurantCostData(costPriceStore.costPriceList, restaurantId);

    // Cross-restaurant sourcing → price enrichment → DishRowData + menu engineering
    // matrix. Shared with the automation review page so both render identical numbers.
    const { enrichedCostPriceList, dishData } = useCostPriceDishData({
        restaurantId,
        revenueItems,
        practice: !!practiceData,
        costTargets,
        defaultWastePercentage,
    });

    // Build a row matcher for the current menu filter.
    // - 'all'           → matches every row (matcher === null acts as the bypass).
    // - 'active_dishes' → matches rows whose menuItemName appeared in revenue history in the last 30 days.
    //                     (We match on menuItemName, not costPriceName: costPriceName can be a custom
    //                     user-edited label, while menuItemName is the link to the POS/revenue item name.)
    // - 'menus'         → matches rows whose cost-price id is in any selected menu.
    // Placeholders are passed through explicitly by the table layer so users can
    // still create cost-price analyses for items they haven't set up yet.
    const activeDishNames = useMemo(
        () => getActiveDishNames(revenueHistoryStore.revenueHistoryList),
        [revenueHistoryStore.revenueHistoryList]
    );

    const matchesMenuFilter = useMemo(() => {
        if (menuMode === 'all') return null;
        if (menuMode === 'active_dishes') {
            return (row: DishRowData) => !!row.menuItemName && activeDishNames.has(row.menuItemName);
        }
        const idSet = new Set<string>();
        menuFilters.forEach(menuId => {
            (menusMenuItems[menuId] || []).forEach(id => idSet.add(id));
        });
        return (row: DishRowData) => idSet.has(row.id);
    }, [menuMode, menuFilters, activeDishNames, menusMenuItems]);

    const isMenuFilterActive = matchesMenuFilter !== null;

    // Whether the restaurant has any CHOICE analysis — gates the Add-ons tab visibility.
    const hasAnyChoiceCostPrice = useMemo(
        () => costPriceStore.costPriceList.some(a => a.costPriceType === COST_PRICE_TYPE_CHOICE),
        [costPriceStore.costPriceList]
    );

    // If the user is sitting on the Add-ons tab and all CHOICE analyses go away
    // (deleted / restaurant changed), fall back to "all" so we never lock them
    // on a hidden tab.
    useEffect(() => {
        if (activeTab === 'addons' && !hasAnyChoiceCostPrice) {
            setActiveTab('all');
        }
    }, [activeTab, hasAnyChoiceCostPrice, setActiveTab]);

    // Get categories that are relevant to the current tab and menu
    const relevantCategories = useMemo(() => {
        // Filter dishData by activeTab and menu (not by category or matrix)
        const tabFilteredData = dishData.filter((row) => {
            // Menu filter - only show categories of items that match the current menu mode
            if (matchesMenuFilter && !matchesMenuFilter(row)) {
                return false;
            }

            // Add-ons tab: CHOICE rows only; every other tab hides them.
            if (activeTab === "addons") {
                if (!row.isChoiceCost) return false;
            } else if (row.isChoiceCost) {
                return false;
            }

            if (activeTab === "food") {
                if (!row.revenueSourceType || row.revenueSourceType !== RevenueSourceType.FOOD) {
                    return false;
                }
            } else if (activeTab === "drinks") {
                if (!row.revenueSourceType || row.revenueSourceType !== RevenueSourceType.DRINKS) {
                    return false;
                }
            }
            return true;
        });

        // Get unique category IDs from the filtered data
        const relevantCategoryIds = new Set(
            tabFilteredData
                .filter(row => row.categoryId && row.categoryId !== '')
                .map(row => row.categoryId)
        );

        // Filter categories to only show those that have items in the current tab/menu
        // Push the default ("Unassigned") category to the end so it appears last in the selector
        return categoriesStore.categories
            .filter(category => relevantCategoryIds.has(category.id))
            .sort((a, b) => {
                if (a.isDefault === b.isDefault) return 0;
                return a.isDefault ? 1 : -1;
            });
    }, [dishData, activeTab, categoriesStore.categories, matchesMenuFilter]);

    // Categories to show in the category assignment modal (filtered by active tab).
    // The Add-ons tab assigns add-on-scoped categories; every other tab uses the
    // revenue scope.
    const modalCategories = useMemo(() => {
        if (activeTab === "addons") {
            return categoriesStore.categories.filter(c => (c.scope ?? 'revenue') === 'addon');
        }
        const revenueCats = categoriesStore.categories.filter(c => (c.scope ?? 'revenue') === 'revenue');
        if (activeTab === "all") return revenueCats;
        const relevantIds = new Set(relevantCategories.map(c => c.id));
        return revenueCats.filter(cat => cat.isDefault || relevantIds.has(cat.id));
    }, [activeTab, categoriesStore.categories, relevantCategories]);

    // Reset selected category to "all" when switching tabs if the current category is not available
    useEffect(() => {
        if (selectedCategoryId !== "all") {
            const categoryStillAvailable = relevantCategories.some(cat => cat.id === selectedCategoryId);
            if (!categoryStillAvailable) {
                setSelectedCategoryId("all");
            }
        }
    }, [activeTab, relevantCategories, selectedCategoryId]);

    // Filter dishData based on activeTab, selectedCategoryId, matrixFilters, and menu
    const filteredDishData = useMemo(() => {
        return dishData.filter((row) => {
            // Filter by menu
            if (matchesMenuFilter && !matchesMenuFilter(row)) {
                return false;
            }

            // CHOICE rows belong only on the Add-ons tab; every other tab excludes them.
            if (activeTab === "addons") {
                if (!row.isChoiceCost) return false;
            } else if (row.isChoiceCost) {
                return false;
            }

            // Filter by tab (All / Food / Drinks)
            if (activeTab === "food") {
                if (!row.revenueSourceType || row.revenueSourceType !== RevenueSourceType.FOOD) {
                    return false;
                }
            } else if (activeTab === "drinks") {
                if (!row.revenueSourceType || row.revenueSourceType !== RevenueSourceType.DRINKS) {
                    return false;
                }
            }
            // For "all" tab, show everything (revenue items + custom items)

            // Filter by category
            if (selectedCategoryId !== "all") {
                if (row.categoryId !== selectedCategoryId) {
                    return false;
                }
            }

            // Filter by matrix (multi-select)
            if (matrixFilters.length > 0) {
                // If row has no matrix (null), exclude it when filters are active
                // (users can explicitly filter for null if needed, but we don't include it by default)
                if (row.matrix === null) {
                    return false;
                }
                // Row has a matrix value, check if it's in the selected filters
                if (!matrixFilters.includes(row.matrix)) {
                    return false;
                }
            }

            return true;
        });
    }, [dishData, activeTab, selectedCategoryId, matrixFilters, matchesMenuFilter]);

    // Filter enrichedCostPriceList based on filtered dishData.
    // Strictly an id projection: the KPI tiles must describe exactly the rows the
    // table shows. Every real dish row carries its analysis id (placeholders carry a
    // synthetic `placeholder-` id and are excluded from the KPIs anyway), so matching
    // by dish NAME as well would re-admit same-named dishes from other menus,
    // categories and tabs that the filters deliberately excluded.
    const filteredEnrichedCostPriceList = useMemo(() => {
        const filteredIds = new Set(filteredDishData.map(d => d.id));
        return enrichedCostPriceList.filter(analysis => filteredIds.has(analysis.id));
    }, [enrichedCostPriceList, filteredDishData]);

    // Filter revenueItems based on filtered dishData
    const filteredRevenueItems = useMemo(() => {
        // Get dish names from filtered dishData that are revenue items (not custom)
        const filteredDishNames = new Set(
            filteredDishData
                .filter(d => !d.isCustomItem)
                .map(d => d.dishName)
        );
        
        return revenueItems.filter(itemName => filteredDishNames.has(itemName));
    }, [revenueItems, filteredDishData]);

    // Calculate KPI metrics for the summary cards using filtered data
    const kpiMetrics = useMemo(() => {
        const baseMetrics = calculateCostPriceKPIs(
            filteredEnrichedCostPriceList,
            revenueHistoryStore.revenueHistoryList,
            filteredRevenueItems,
            showExVatOrders,
            // In a filtered view (specific menus or active dishes) scope the
            // Total Analyzed denominator to the filtered revenue items only.
            menuMode !== 'all'
        );

        const highestMarginItem = getItemWithHighestMargin(
            filteredEnrichedCostPriceList,
            revenueHistoryStore.revenueHistoryList,
            showExVatOrders
        );

        const mostSoldItem = getMostSoldItem(
            filteredEnrichedCostPriceList,
            revenueHistoryStore.revenueHistoryList,
            filteredRevenueItems
        );

        return {
            ...baseMetrics,
            highestMarginItem,
            mostSoldItem
        };
    }, [filteredEnrichedCostPriceList, revenueHistoryStore.revenueHistoryList, filteredRevenueItems, showExVatOrders, menuMode]);

    // Handler for item click - open detail modal
    const handleItemClick = async (item: DishRowData) => {
        if (item.id.startsWith('placeholder-')) {
            // Item doesn't exist in DB yet - create it first using reusable utility
            if (!restaurantId) return;

            try {
                const newAnalysis = await createCostPriceFromPlaceholder(
                    {
                        costPriceName: item.costPriceName,
                        menuItemName: item.menuItemName,
                        restaurantId
                    },
                    categoriesStore,
                    costPriceStore,
                    createCostPriceAnalysis
                );

                // Auto-assign to selected menu only when exactly one real menu is selected
                if (menuMode === 'menus' && menuFilters.length === 1 && newAnalysis.id) {
                    const targetMenuId = menuFilters[0];
                    try {
                        await addItemsToMenu(targetMenuId, [newAnalysis.id]);
                        await menusLoadMenuItems(targetMenuId);
                    } catch (error) {
                        console.error('Failed to add item to menu:', error);
                    }
                }

                // Open the new analysis in modal
                setSelectedCostPriceId(newAnalysis.id);
            } catch (error) {
                console.error('Failed to create cost price analysis:', error);
                alert(t('costPrice.failedToCreateAnalysis'));
            }
        } else {
            // Item exists - open modal directly
            setSelectedCostPriceId(item.id);
        }
    };

    // Handler for creating a custom cost price item
    const handleCreateCustomItem = async (name: string, price: number, priceExVat: number, categoryId: string, costPriceType?: string) => {
        if (!restaurantId) return;

        try {
            const createRequest: CostPriceCreateRequest = {
                restaurantId,
                costPriceName: name,
                menuItemName: undefined, // Custom items are not linked to revenue items by default
                categoryId,
                ingredients: [],
                isCustomItem: true,
                customItemPrice: price,
                customItemPriceExVat: priceExVat,
                costPriceType
            };

            const newAnalysis = await createCostPriceAnalysis(createRequest);

            // Auto-assign to selected menu only when exactly one real menu is selected
            if (menuMode === 'menus' && menuFilters.length === 1 && newAnalysis.id) {
                const targetMenuId = menuFilters[0];
                try {
                    await addItemsToMenu(targetMenuId, [newAnalysis.id]);
                    await menusLoadMenuItems(targetMenuId);
                } catch (error) {
                    console.error('Failed to add item to menu:', error);
                }
            }

            await costPriceStore.load(restaurantId);

            // Open the new analysis in modal
            setSelectedCostPriceId(newAnalysis.id);
        } catch (error) {
            console.error('Failed to create custom cost price item:', error);
            throw error;
        }
    };

    // Handler for closing detail modal. When the user is editing a CHOICE that
    // came from a BYO dish, hop back to the parent BYO instead of closing.
    const handleCloseDetailModal = () => {
        if (parentByoCostPriceId) {
            setSelectedCostPriceId(parentByoCostPriceId);
            setParentByoCostPriceId(null);
            return;
        }
        setSelectedCostPriceId(null);
    };

    // BYO: lazy find-or-create the CHOICE analysis for `choiceName`, then swap
    // the modal to that analysis (remembering the parent BYO so close hops back).
    const handleEditChoice = async (choiceName: string, parentCategoryId: string | undefined) => {
        if (!restaurantId || !selectedCostPriceId) return;
        try {
            const choice = await findOrCreateChoiceCostPrice(
                restaurantId,
                choiceName,
                parentCategoryId,
                costPriceStore.costPriceList,
            );
            // Reload the store so the new CHOICE is part of `byId` lookups.
            await costPriceStore.load(restaurantId);
            setParentByoCostPriceId(selectedCostPriceId);
            setSelectedCostPriceId(choice.id);
        } catch (error) {
            console.error('Failed to open choice:', error);
            alert(t('costPrice.failedToCreateAnalysis'));
        }
    };

    // Handler functions for category editing
    const handleChangeItemCategory = async (itemName: string, newCategoryId: string) => {
        if (!restaurantId) return;

        // Find existing analysis or create new one
        const existingAnalysis = costPriceStore.costPriceList.find(
            analysis => analysis.costPriceName === itemName || analysis.menuItemName === itemName
        );

        try {
            if (existingAnalysis) {
                // Update existing analysis
                await updateCostPriceAnalysis({
                    id: existingAnalysis.id,
                    categoryId: newCategoryId
                });
            } else {
                // Create new analysis
                await createCostPriceAnalysis({
                    restaurantId,
                    costPriceName: itemName,
                    menuItemName: itemName,
                    categoryId: newCategoryId,
                    ingredients: []
                });
            }

            await costPriceStore.load(restaurantId);
            setEditingItemCategory(null);
        } catch (error) {
            console.error('Failed to change item category:', error);
            alert(t('costPrice.failedToChangeCategory'));
        }
    };

    const handleCreateAndAssignCategory = async (categoryName: string) => {
        if (!restaurantId || !categoryName.trim() || !editingItemCategory) return;

        try {
            // Create new category (color will be auto-assigned by backend).
            // The Add-ons tab creates add-on-scoped categories.
            const newCategory = await createCostPriceCategory({
                restaurantId,
                name: categoryName.trim(),
                scope: activeTab === 'addons' ? 'addon' : 'revenue'
            });

            // Reload categories
            await categoriesStore.reload();

            // Assign item to new category
            await handleChangeItemCategory(editingItemCategory.itemName, newCategory.id);
        } catch (error) {
            console.error('Failed to create and assign category:', error);
            alert(t('costPrice.failedToCreateAndAssignCategory'));
        }
    };

    const handleReorderCategories = async (reorderedCategories: any[]) => {
        if (!restaurantId) return;

        const updates = reorderedCategories
            .filter(cat => !cat.isDefault)
            .map(cat => ({ id: cat.id, position: cat.position }));

        // Apply locally first so the dropped item stays put (no snap-back)
        categoriesStore.applyPositions(updates);

        try {
            await updateCategoryPositions(updates);
            await categoriesStore.reload();
        } catch (error) {
            console.error('Failed to reorder categories:', error);
            alert(t('costPrice.failedToReorderCategories'));
            await categoriesStore.reload();
        }
    };

    // Reorder triggered from the cost price page tabs. Tabs only show a subset of
    // categories (filtered by active tab + menu + items present). Hidden categories
    // must keep their original positions, so visible categories are re-stamped onto
    // the position slots they collectively occupied, in the new visual order.
    const handleReorderTabsCategories = async (visibleReordered: any[]) => {
        if (!restaurantId) return;

        const reorderable = visibleReordered.filter(cat => !cat.isDefault);
        if (reorderable.length === 0) return;

        const visibleIds = new Set(reorderable.map(c => c.id));
        const occupiedSlots = categoriesStore.categories
            .filter(c => visibleIds.has(c.id))
            .map(c => c.position)
            .sort((a, b) => a - b);

        const updates = reorderable.map((cat, i) => ({ id: cat.id, position: occupiedSlots[i] }));

        // Apply locally first so the dropped tab stays put (no snap-back)
        categoriesStore.applyPositions(updates);

        try {
            await updateCategoryPositions(updates);
            await categoriesStore.reload();
        } catch (error) {
            console.error('Failed to reorder categories:', error);
            alert(t('costPrice.failedToReorderCategories'));
            await categoriesStore.reload();
        }
    };

    // Handler for category badge click
    const handleCategoryClick = (item: DishRowData) => {
        if (isPracticeActive() || isPracticeId(item.id)) return;
        const currentAnalysis = costPriceStore.costPriceList.find(
            analysis => analysis.costPriceName === item.dishName || analysis.menuItemName === item.dishName
        );

        setEditingItemCategory({
            itemName: item.dishName,
            // Add-ons use null=unassigned (no default row); revenue items fall back
            // to the restaurant's default "Unassigned" category.
            currentCategoryId: currentAnalysis?.categoryId
                || (activeTab === 'addons' ? '' : categoriesStore.getDefaultCategory()?.id || '')
        });
    };

    // Handler for sort column click
    const handleSort = (column: string) => {
        if (sortColumn === column) {
            // Toggle direction if same column
            const newDirection = sortDirection === 'asc' ? 'desc' : 'asc';
            setCostPriceSort(column, newDirection);
        } else {
            // Set new column with ascending direction
            setCostPriceSort(column, 'asc');
        }
    };

    // Export / copy of the rows currently shown in the table. Column order mirrors
    // the table (with the linked revenue item name added, which the table doesn't
    // have room for).
    const exportHeaders = [
        t('costPrice.table.dishName'),
        t('costPrice.export.menuItemName'),
        t('costPrice.table.category'),
        t('costPrice.table.matrix'),
        t('costPrice.table.cost'),
        t('costPrice.table.price'),
        t('costPrice.table.costPercentage'),
        t('costPrice.table.margin'),
        t('costPrice.table.popularity'),
    ];

    // The detailed export keeps those nine dish columns and adds the recipe
    // columns the ingredient / subrecipe / add-on lines underneath each dish use.
    const detailedExportHeaders = [
        t('costPrice.export.detailed.type'),
        t('costPrice.export.detailed.name'),
        t('costPrice.export.menuItemName'),
        t('costPrice.table.category'),
        t('costPrice.table.matrix'),
        t('costPrice.export.detailed.supplier'),
        t('costPrice.export.detailed.quantity'),
        t('costPrice.export.detailed.unit'),
        t('costPrice.export.detailed.waste'),
        t('costPrice.export.detailed.unitPrice'),
        t('costPrice.table.cost'),
        t('costPrice.table.price'),
        t('costPrice.table.costPercentage'),
        t('costPrice.table.margin'),
        t('costPrice.table.popularity'),
    ];

    const exportFileName = (suffix: string) => {
        const restaurantName = (selectedRestaurant?.name || 'restaurant').replace(/[^a-z0-9]+/gi, '-').toLowerCase();
        return `cost-price${suffix}-${restaurantName}-${new Date().toISOString().slice(0, 10)}.csv`;
    };

    const handleExportCsv = () => {
        downloadCsv(buildCostPriceCsv(visibleRows, exportHeaders), exportFileName(''));
    };

    const handleExportDetailedCsv = () => {
        const csv = buildCostPriceDetailedCsv(visibleRows, detailedExportHeaders, {
            orderHistory: orderHistoryList,
            customIngredients: costPriceStore.customIngredients,
            subrecipesById,
            allAnalyses: enrichedCostPriceList,
            showExVat: showExVatOrders,
            defaultWastePercentage,
            byRestaurant,
            currentRestaurantId: restaurantId,
            labels: {
                dish: t('costPrice.export.detailed.typeDish'),
                ingredient: t('costPrice.export.detailed.typeIngredient'),
                alternative: t('costPrice.export.detailed.typeAlternative'),
                subrecipe: t('costPrice.export.detailed.typeSubrecipe'),
                menuItem: t('costPrice.export.detailed.typeMenuItem'),
                addOn: t('costPrice.export.detailed.typeAddOn'),
                picksUnit: t('costPrice.export.detailed.picksUnit'),
                deletedSubrecipe: t('costPrice.subrecipes.deletedSubrecipeLabel'),
            },
        });
        downloadCsv(csv, exportFileName('-ingredients'));
    };

    const handleCopyToClipboard = async (): Promise<boolean> => {
        try {
            await navigator.clipboard.writeText(buildCostPriceClipboardText(visibleRows, exportHeaders));
            return true;
        } catch (error) {
            console.error('Failed to copy cost price data to clipboard:', error);
            return false;
        }
    };

    if (isAllRestaurants && !searchParams.has('customerRequest')) {
        return (
            <div className="p-4 max-w-7xl mx-auto">
                <MobilePageHeader title={t('costPrice.title')} right={<RestaurantSelector />} />
                <div className="mb-6 hidden lg:block">
                    <h1 className="text-3xl font-bold text-textPrimary dark:text-textPrimary-dark mb-4">
                        {t('costPrice.title')}
                    </h1>
                </div>

                <div className="bg-yellow-50 dark:bg-yellow-950/30 border border-yellow-200 dark:border-yellow-800 rounded-lg p-4">
                    <p className="text-yellow-800 dark:text-yellow-200">
                        {t('costPrice.create.pleaseSelectSpecificRestaurant')}
                    </p>
                </div>
            </div>
        );
    }

    return (
        <div data-onboarding="cost-page">
            <MobilePageHeader title={t('costPrice.title')} right={<RestaurantSelector />} />
            <Header
                activeTab={activeTab}
                onTabChange={setActiveTab}
                showExVatOrders={showExVatOrders}
                onCreateCustomItem={() => setShowCreateCustomModal(true)}
                onOpenAutomation={() => setAutomationVariant('standard')}
                onOpenNewMenuAutomation={practiceData ? undefined : () => setAutomationVariant('new_menu')}
                onOpenCustomLink={() => setShowCustomLinkModal(true)}
                onOpenAlternativesLink={() => setShowAlternativesLinkModal(true)}
                onOpenDelete={() => setShowDeleteModal(true)}
                onExportCsv={handleExportCsv}
                onExportDetailedCsv={handleExportDetailedCsv}
                onCopyToClipboard={handleCopyToClipboard}
                showAutomationButton={canAccessAutomation}
                showCustomLinkButton={canAccessAutomation}
                showDeleteButton={canAccessAutomation}
                showExportOptions={canAccessAutomation}
                showAddOnsTab={hasAnyChoiceCostPrice}
            />

            <Cards kpiMetrics={kpiMetrics} activeTab={activeTab} costTargets={costTargets} />
            <AutomationRunBanner />
            <CostPriceTable
                data={dishData}
                activeTab={activeTab}
                selectedCategoryId={selectedCategoryId}
                onCategoryChange={setSelectedCategoryId}
                onDishClick={handleItemClick}
                onCategoryClick={practiceData ? undefined : handleCategoryClick}
                matrixFilters={matrixFilters}
                onMatrixFiltersChange={setMatrixFilters}
                sortColumn={sortColumn}
                sortDirection={sortDirection}
                onSort={handleSort}
                filteredCategories={relevantCategories}
                onReorderCategories={handleReorderTabsCategories}
                menus={menusMenus}
                menuFilters={menuFilters}
                onMenuFiltersChange={setMenuFilters}
                isMenuFilterActive={isMenuFilterActive}
                matchesMenuFilter={matchesMenuFilter}
                costTargets={costTargets}
                onVisibleRowsChange={setVisibleRows}
            />

            {/* Create Custom Cost Price Modal */}
            {showCreateCustomModal && restaurantId && (
                <CreateCustomCostPriceModal
                    restaurantId={restaurantId}
                    onClose={() => setShowCreateCustomModal(false)}
                    onCreate={handleCreateCustomItem}
                />
            )}

            {searchParams.get('customerRequest') && <CustomerAlternativeRoute
                key={searchParams.get('customerRequest')!}
                requestId={searchParams.get('customerRequest')!}
                onClose={() => {
                    const next = new URLSearchParams(searchParams);
                    next.delete('customerRequest');
                    setSearchParams(next, { replace: true });
                }}
            />}

            {/* Cost Price Detail Modal */}
            {selectedCostPriceId && !searchParams.has('customerRequest') && (
                <CostPriceDetailModal
                    // Key on id so React fully remounts when hopping between
                    // a BYO dish and a CHOICE — clears the panel-visible animation
                    // state and refetches enrichment cleanly.
                    key={selectedCostPriceId}
                    costPriceId={selectedCostPriceId}
                    onClose={handleCloseDetailModal}
                    costTargets={costTargets}
                    defaultWastePercentage={defaultWastePercentage}
                    onEditChoice={handleEditChoice}
                    // When the user is editing a CHOICE that came from a BYO
                    // dish, show a back arrow (and hide the close icon) so the
                    // return-to-parent path is the obvious one.
                    onBack={parentByoCostPriceId ? handleCloseDetailModal : undefined}
                    // Dish context: quantity edits inside the CHOICE editor are
                    // stored as per-dish overrides on this parent BYO dish.
                    byoParentId={parentByoCostPriceId ?? undefined}
                />
            )}

            {/* Category Assignment Modal */}
            {!practiceData && editingItemCategory && (
                <CategoryAssignmentModal
                    itemName={editingItemCategory.itemName}
                    currentCategoryId={editingItemCategory.currentCategoryId}
                    categories={modalCategories}
                    onSelectCategory={(categoryId) => handleChangeItemCategory(editingItemCategory.itemName, categoryId)}
                    onCreateAndAssignCategory={handleCreateAndAssignCategory}
                    onReorderCategories={handleReorderCategories}
                    onCategoryUpdated={async () => {
                        await categoriesStore.reload();
                    }}
                    onClose={() => setEditingItemCategory(null)}
                />
            )}

            {/* Cost Price Automation Modal — automation doesn't apply to CHOICE
                rows, so when the Add-ons tab is active we fall back to 'all'. */}
            {automationVariant && restaurantId && (
                <CostPriceAutomationModal
                    restaurantId={restaurantId}
                    overallType={activeTab === 'addons' ? 'all' : activeTab}
                    variant={automationVariant}
                    defaultMenuId={automationVariant === 'standard' && menuMode === 'menus' && menuFilters.length === 1 ? menuFilters[0] : undefined}
                    onMenuImported={(menuId) => setMenuFilters([menuId])}
                    onClose={() => setAutomationVariant(null)}
                />
            )}

            {showCustomLinkModal && restaurantId && (
                <LinkCustomIngredientsBulkModal
                    isOpen={showCustomLinkModal}
                    onClose={() => setShowCustomLinkModal(false)}
                    restaurantId={restaurantId}
                    defaultWastePercentage={defaultWastePercentage}
                />
            )}

            {showAlternativesLinkModal && restaurantId && canAccessAutomation && (
                <LinkAlternativesBulkModal
                    defaultWastePercentage={defaultWastePercentage}
                    key={restaurantId}
                    isOpen={showAlternativesLinkModal}
                    onClose={() => setShowAlternativesLinkModal(false)}
                    restaurantId={restaurantId}
                />
            )}

            {/* Cost Price Delete Modal */}
            {showDeleteModal && restaurantId && (
                <CostPriceDeleteModal
                    restaurantId={restaurantId}
                    dishData={dishData}
                    onClose={() => setShowDeleteModal(false)}
                    onSuccess={async () => {
                        await costPriceStore.load(restaurantId, { force: true });
                        await costPriceStore.loadAutomationRuns(restaurantId);
                        await categoriesStore.reload();
                    }}
                />
            )}
        </div>
    );
};

export default CostPricePageNew;
